import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  H2,
  H3,
  Input,
  Modal,
  PasswordInput,
  Preloader,
  Select,
  Toggle,
} from '@smartshell/ui-kit';
import { useForm, Controller } from 'react-hook-form';

import { AuthContext } from 'auth';
import { IAccountInput } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  GET_HOSTS_FOR_GAME_ACCOUNTS,
  IGetHostsForGameAccountResponse,
} from 'schemas';

import { GameAccountsContext } from '../GameAccounts';
import {
  IFormGameAccountValues,
  IManageGameAccountsMWProps,
} from '../interfaces';
import s from './ManageGameAccountMW.module.scss';
import { getHostOptions } from './utils';

export const ManageGameAccountMW: FC<IManageGameAccountsMWProps> = ({
  onClose,
  group,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { accountForEdit, createAccount, updateAccount, deleteAccount } =
    useContext(GameAccountsContext) ?? {};

  const { data, loading } = useQuery<IGetHostsForGameAccountResponse>(
    GET_HOSTS_FOR_GAME_ACCOUNTS
  );

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isDirty },
    watch,
    setValue,
  } = useForm<IFormGameAccountValues>({
    mode: 'onChange',
    defaultValues: {
      login: '',
      password: '',
      active: true,
      host_group_id: null,
      host_id: null,
    },
  });

  const [isRemoveInProgress, setIsRemoveInProgress] = useState(false);

  const currentGroupId = watch('host_group_id');

  const { hostGroupOptions, hostOptions } = useMemo(
    () => getHostOptions(data),
    [data]
  );

  useEffect(() => {
    if (accountForEdit) {
      const { login, password, active, assignment } = accountForEdit;

      const resetData: IFormGameAccountValues = {
        login,
        password: password || '',
        active: active === undefined ? true : active,
        host_id: null,
        host_group_id: null,
      };

      if (assignment) {
        const {
          host: { id, group_id },
        } = assignment;
        resetData.host_id = id;
        resetData.host_group_id = group_id;
      }

      reset(resetData);
    }
  }, [accountForEdit, hostGroupOptions, hostOptions]);

  return (
    <Modal onClose={onClose}>
      <Preloader loading={loading}>
        <form
          onSubmit={handleSubmit(({ password, login, active, host_id }) => {
            if (!createAccount || !updateAccount) {
              return;
            }

            const saveDate: IAccountInput = {
              group_id: group.id,
              login,
              active,
              password,
              host_id,
            };

            if (accountForEdit) {
              return updateAccount(accountForEdit.id, saveDate).then(onClose);
            }

            return createAccount(saveDate).then(onClose);
          })}
          className={s.form}
        >
          <H2 className={s.header}>
            {accountForEdit
              ? t('account.editing')
              : t('account.creating', group.title)}
          </H2>
          <div className={s.group}>
            <H3>{t('account.data')}</H3>
            <div className={s.fields}>
              <Controller
                name="login"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    title={t('login')}
                    placeholder={t('login.insert')}
                    error={error?.message}
                  />
                )}
                rules={{
                  required: t('errors.required'),
                  minLength: {
                    value: 2,
                    message: t('errors.minLength', 2),
                  },
                  maxLength: {
                    value: 64,
                    message: t('errors.maxLength', 64),
                  },
                }}
              />

              <Controller
                name="password"
                control={control}
                render={({ field, fieldState: { error } }) =>
                  permissions?.has('game_account_password_view') ? (
                    <PasswordInput
                      {...field}
                      autoComplete="off"
                      title={t('password')}
                      error={error?.message}
                      className={s.password}
                    />
                  ) : (
                    <Input
                      {...field}
                      title={t('password')}
                      error={error?.message}
                      type="password"
                      className={s.password}
                    />
                  )
                }
                rules={{
                  required: t('errors.required'),
                  minLength: {
                    value: 6,
                    message: t('errors.minLength', 6),
                  },
                  maxLength: {
                    value: 255,
                    message: t('errors.maxLength', 255),
                  },
                }}
              />
            </div>
          </div>
          <div className={s.group}>
            <H3>{t('additional')}</H3>
            <div className={s.fields}>
              <Controller
                name="active"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    value={value}
                    onChange={onChange}
                    iconName="ShutdownIcon"
                    label={t('account.status.active')}
                  />
                )}
              />
            </div>
          </div>
          <div className={s.group}>
            <H3>{t('account.host')}</H3>
            <Controller
              control={control}
              render={({ field }) => (
                <Select
                  title={t('report.hosts.group')}
                  options={hostGroupOptions}
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                    setValue('host_id', null);
                  }}
                />
              )}
              name="host_group_id"
            />
            <Controller
              control={control}
              render={({ field }) => (
                <Select
                  title={t('host')}
                  options={hostOptions.filter(
                    ({ original }) => original?.group_id === currentGroupId
                  )}
                  {...field}
                  disabled={!currentGroupId}
                />
              )}
              name="host_id"
            />
          </div>
          <div className={s.controls}>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty || isRemoveInProgress}
              fullWidth
            >
              {t(accountForEdit ? 'save' : 'add')}
            </Button>
            {accountForEdit && (
              <Button
                onClick={() => {
                  if (!deleteAccount) {
                    return;
                  }

                  setIsRemoveInProgress(true);
                  deleteAccount(accountForEdit.id)
                    .then(onClose)
                    .finally(() => setIsRemoveInProgress(false));
                }}
                disabled={isSubmitting || isRemoveInProgress}
                className={s.remove}
                variant="secondary"
                color="red"
                fullWidth
              >
                {t('delete')}
              </Button>
            )}
          </div>
        </form>
      </Preloader>
    </Modal>
  );
};
